package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.EventRow
import com.arenaproplay.admin.domain.admin.PageResult
import com.arenaproplay.admin.domain.admin.SeedCardTemplate
import com.arenaproplay.admin.domain.admin.SeedCustomPostRequest
import com.arenaproplay.admin.domain.admin.SeedCustomStoryRequest
import com.arenaproplay.admin.domain.admin.SeedCustomUserRequest
import com.arenaproplay.admin.domain.admin.SeedParticipant
import com.arenaproplay.admin.domain.admin.SeedEventRequest
import com.arenaproplay.admin.domain.admin.SeedParticipantsRequest
import com.arenaproplay.admin.domain.admin.SeedUserRow
import com.arenaproplay.admin.domain.admin.SportOption
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.forms.AddressAutocompleteField
import com.arenaproplay.admin.ui.components.forms.Dropdown
import com.arenaproplay.admin.ui.components.forms.DropdownOption
import com.arenaproplay.admin.ui.components.forms.TextArea
import com.arenaproplay.admin.ui.components.forms.TextInput
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.ui.components.overlay.Modal
import com.arenaproplay.admin.ui.components.tabs.TabItem
import com.arenaproplay.admin.ui.components.tabs.Tabs
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.pickImageFile
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

// Languages are stored as display names; interests as lowercase catalog ids —
// both must match the client-side InterestCatalog / Languages lists so the
// app can render seeded profiles.
private val SEED_LANGUAGES = listOf("Ukrainian", "English", "Polish", "German", "Spanish", "Italian")
private val SEED_ACTIVITIES = listOf(
    "cycling" to "Cycling",
    "gym" to "Gym",
    "travel" to "Travel",
    "camping" to "Camping",
)
private val SEED_LIFESTYLE = listOf(
    "fishing" to "Fishing",
    "music" to "Music",
    "reading" to "Reading",
    "art" to "Art",
    "photography" to "Photography",
    "cooking" to "Cooking",
)
private const val RANDOM = "__random__"

private fun String.interestLabel(): String = replaceFirstChar { it.uppercase() }

// Same six post layouts as the app's create-post popup.
private val POST_LAYOUTS = listOf(
    1 to "1 фото (портрет)",
    2 to "1 фото (пейзаж)",
    3 to "2 рядом",
    4 to "2 стопкой",
    5 to "1 широкое + 2",
    6 to "2 + 1 широкое",
)

private fun slotsFor(layout: Int): Int = when (layout) {
    1, 2 -> 1
    3, 4 -> 2
    5, 6 -> 3
    else -> 1
}

/** "2026-08-02" + "18:30" in the browser's zone → UTC ISO instant string. */
private fun localToUtcIso(date: String, time: String): String =
    kotlin.js.Date("${date}T$time:00").toISOString()

// ── Shared banner composable ───────────────────────────────────────────────────

@Composable
private fun StatusBanner(message: String, isError: Boolean) {
    val bg = if (isError) AppColors.Accent.DangerTint else AppColors.Accent.SuccessTint
    val fg = if (isError) AppColors.Error else AppColors.Success
    val border = if (isError) AppColors.Error.toRgbaString(0.22f) else AppColors.Success.toRgbaString(0.22f)
    Box(
        Modifier.fillMaxWidth().sBorderRadius(10).sPadding(10, 14)
            .styleModifier {
                property("background", bg.toRgbString())
                property("border", "1px solid $border")
            }
    ) {
        SpanText(message, Modifier.bodySMedium().color(fg))
    }
}

// ─────────────────────────────────────────────────────────────────────────────

@Page("/content-studio")
@Composable
fun ContentStudioPage() {
    val repo = LocalAppContainer.current.adminRepository ?: return
    val scope = rememberCoroutineScope()

    var purgeConfirmOpen by remember { mutableStateOf(false) }
    var purging by remember { mutableStateOf(false) }
    var purgeError by remember { mutableStateOf<String?>(null) }
    // Bumped after any mutation so all tabs refetch on next show.
    var reloadToken by remember { mutableStateOf(0) }

    AdminLayout(
        title = "Content Studio",
        subtitle = "Наполнение платформы тестовыми пользователями, аватарами, публикациями и событиями",
        actions = {
            // Danger action — opens ConfirmActionOverlay
            Box(
                Modifier
                    .sBorderRadius(999)
                    .padding(leftRight = 16.px, topBottom = 8.px)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("background", AppColors.ErrorTint.toRgbString())
                        property("border", "1px solid ${AppColors.Error.toRgbaString(0.18f)}")
                        property("transition", "opacity 140ms ease")
                    }
                    .onClick { purgeConfirmOpen = true },
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    LucideIcon(name = "trash-2", size = 14, color = AppColors.Error.toRgbString())
                    Box(Modifier.width(7.px))
                    SpanText(
                        "Очистить тестовые данные",
                        Modifier.bodySMedium().color(AppColors.Error)
                            .styleModifier { property("font-weight", "600") },
                    )
                }
            }
        },
    ) {
        Tabs(
            tabs = listOf(
                TabItem("users", "Пользователи") {
                    SeedUsersTab(repo, scope, reloadToken) { reloadToken++ }
                },
                TabItem("posts", "Публикации") {
                    SeedPostsTab(repo, scope, reloadToken)
                },
                TabItem("events", "События") {
                    SeedEventsTab(repo, scope, reloadToken) { reloadToken++ }
                },
            ),
        )
        VSpace(28)
    }

    // ── Purge confirmation overlay ─────────────────────────────────────────────
    ConfirmActionOverlay(
        isOpen = purgeConfirmOpen,
        onClose = { if (!purging) { purgeConfirmOpen = false; purgeError = null } },
        title = "Очистить все тестовые данные",
        description = "Будут безвозвратно удалены все тестовые пользователи вместе с их публикациями, " +
            "историями, аватарами и событиями. Реальные пользователи не затрагиваются. " +
            "Операция необратима.",
        warning = "Внимание: после очистки все вкладки обнуляются. Убедитесь, что это действительно нужно.",
        error = purgeError,
        confirmLabel = if (purging) "Очищаем…" else "Очистить всё",
        confirmDanger = true,
        confirmEnabled = !purging,
    ) {
        purging = true
        purgeError = null
        scope.launch {
            try {
                repo.purgeSeedData()
                purgeConfirmOpen = false
                purgeError = null
                reloadToken++
            } catch (e: Throwable) {
                purgeError = e.message ?: "Ошибка очистки данных"
            } finally {
                purging = false
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Users tab — one-at-a-time creation and editing
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SeedUsersTab(
    repo: AdminRepository,
    scope: CoroutineScope,
    reloadToken: Int,
    onMutated: () -> Unit,
) {
    var page by remember { mutableStateOf(0) }
    var data by remember { mutableStateOf<PageResult<SeedUserRow>?>(null) }
    var localReload by remember { mutableStateOf(0) }
    val loadState = rememberLoadState()
    var banner by remember { mutableStateOf<Pair<String, Boolean>?>(null) }

    var formOpen by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<SeedUserRow?>(null) }

    LaunchedEffect(page, reloadToken, localReload, loadState.reloadToken) {
        data = null
        loadState.load {
            data = repo.getSeedUsers(page, 25)
        }
    }

    Column(Modifier.fillMaxWidth()) {
        if (loadState.error != null) {
            ErrorCard(loadState)
        } else {
            SectionCard(
                title = "Тестовые пользователи",
                subtitle = "Создаются по одному. Каждое пустое поле генерируется автоматически. PRO+ всегда включён.",
                actions = {
                    StudioActionButton("+ Новый пользователь", primary = true) {
                        editing = null
                        formOpen = true
                    }
                },
            ) {
                banner?.let { (msg, isError) ->
                    StatusBanner(msg, isError)
                    VSpace(12)
                }

                DataTable(
                    items = data?.items.orEmpty(),
                    loading = data == null,
                    columns = listOf(
                        TableColumn<SeedUserRow>("ПОЛЬЗОВАТЕЛЬ", 2f) { row ->
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                SeedAvatarBubble(row.avatarUrl, row.fullName)
                                Box(Modifier.width(12.px))
                                Column {
                                    SpanText(row.fullName, Modifier.bodyMMedium().color(AppColors.Ink))
                                    Box(Modifier.height(3.px))
                                    SpanText("@${row.username}", Modifier.bodySMedium().color(AppColors.Muted))
                                }
                            }
                        },
                        TableColumn<SeedUserRow>("ГОРОД", 0.9f) {
                            SpanText(it.city ?: "—", Modifier.bodyMRegular().color(AppColors.Muted))
                        },
                        TableColumn<SeedUserRow>("ВИДЫ СПОРТА", 1.3f) { row ->
                            SpanText(
                                row.interestSports.joinToString(", ") { it.interestLabel() }.ifBlank { "—" },
                                Modifier.bodyMRegular().color(AppColors.Muted),
                            )
                        },
                        TableColumn<SeedUserRow>("СТАТУС", 0.6f) {
                            Pill("PRO+", PillTone.Pro)
                        },
                        TableColumn<SeedUserRow>("СОЗДАН", 0.9f) {
                            SpanText(
                                formatTimestampShort(it.createdAt),
                                Modifier.bodyMRegular().color(AppColors.Muted),
                            )
                        },
                        TableColumn<SeedUserRow>("", 1.2f) { row ->
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                StudioActionButton("Изменить", small = true) {
                                    editing = row
                                    formOpen = true
                                }
                                Box(Modifier.width(8.px))
                                StudioActionButton("Удалить", small = true, danger = true) {
                                    scope.launch {
                                        runCatching { repo.deleteUser(row.id) }
                                        localReload++
                                        onMutated()
                                    }
                                }
                            }
                        },
                    ),
                    emptyText = "Тестовых пользователей нет — создайте первого",
                )
                VSpace(16)
                PaginationBar(
                    page = page,
                    pageSize = 25,
                    total = data?.total ?: 0,
                    onPrev = { if (page > 0) page-- },
                    onNext = { if ((page + 1) * 25 < (data?.total ?: 0)) page++ },
                )
            }
        }
    }

    if (formOpen) {
        UserFormModal(
            repo = repo,
            scope = scope,
            existing = editing,
            onClose = { formOpen = false },
            onSaved = { user, created ->
                banner = (if (created) "Создан ${user.fullName} (@${user.username})" else "Изменения сохранены для ${user.fullName}") to false
                localReload++
                onMutated()
            },
        )
    }
}

/**
 * Create/edit form for a single fake user — the same fields as the app's
 * profile. Location comes from address autocomplete so coordinates are saved
 * with the city. In edit mode a card gallery + AI avatar generation section
 * appears below, mirroring the app's card flow.
 */
@Composable
private fun UserFormModal(
    repo: AdminRepository,
    scope: CoroutineScope,
    existing: SeedUserRow?,
    onClose: () -> Unit,
    onSaved: (SeedUserRow, created: Boolean) -> Unit,
) {
    var sports by remember { mutableStateOf<List<SportOption>>(emptyList()) }
    LaunchedEffect(Unit) {
        runCatching { sports = repo.getSports().filter { it.name != "Other" } }
    }

    var fullName by remember { mutableStateOf(existing?.fullName ?: "") }
    var username by remember { mutableStateOf(existing?.username ?: "") }
    var email by remember { mutableStateOf(existing?.email ?: "") }
    var gender by remember { mutableStateOf(existing?.gender ?: RANDOM) }
    var dateOfBirth by remember { mutableStateOf(existing?.dateOfBirth ?: "") }
    var locationQuery by remember { mutableStateOf(existing?.city ?: "") }
    var city by remember { mutableStateOf(existing?.city ?: "") }
    var country by remember { mutableStateOf(existing?.country ?: "") }
    var cityLat by remember { mutableStateOf(existing?.cityLat) }
    var cityLng by remember { mutableStateOf(existing?.cityLng) }
    var bio by remember { mutableStateOf(existing?.bio ?: "") }
    var selectedSports by remember { mutableStateOf(existing?.interestSports?.toSet() ?: setOf()) }
    var languages by remember { mutableStateOf(existing?.languages?.toSet() ?: setOf()) }
    var activities by remember { mutableStateOf(existing?.interestActivities?.toSet() ?: setOf()) }
    var lifestyle by remember { mutableStateOf(existing?.interestLifestyle?.toSet() ?: setOf()) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    Modal(
        isOpen = true,
        onClose = { if (!saving) onClose() },
        title = if (existing == null) "Новый тестовый пользователь" else "Изменить ${existing.fullName}",
        maxWidth = 680.px,
        closeOnOverlayClick = false,
        modifier = Modifier.styleModifier {
            property("max-height", "88vh")
            property("overflow-y", "auto")
        },
    ) {
        Column(Modifier.fillMaxWidth()) {
            if (existing == null) {
                SpanText(
                    "Заполните только нужные поля — остальные будут сгенерированы автоматически. PRO+ всегда включён.",
                    Modifier.bodySMedium().color(AppColors.Muted),
                )
                VSpace(14)
            }
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(fullName, { fullName = it }, label = "Полное имя", placeholder = "авто")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(username, { username = it }, label = "Username", placeholder = "авто")
                }
            }
            VSpace(10)
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(email, { email = it }, label = "Email", placeholder = "авто (@seed.arenaproplay.com)")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(dateOfBirth, { dateOfBirth = it }, label = "Дата рождения", placeholder = "ГГГГ-ММ-ДД, авто")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    Dropdown(
                        options = listOf(
                            DropdownOption(RANDOM, "Авто"),
                            DropdownOption("Male", "Мужской"),
                            DropdownOption("Female", "Женский"),
                        ),
                        selectedValue = gender,
                        onSelect = { gender = it.value },
                        label = "Пол",
                    )
                }
            }
            VSpace(10)
            AddressAutocompleteField(
                value = locationQuery,
                onValueChange = {
                    locationQuery = it
                    // Free typing invalidates the picked coordinates.
                    city = it
                    cityLat = null
                    cityLng = null
                },
                onResolved = { resolved ->
                    locationQuery = resolved.address
                    city = resolved.city ?: resolved.address.substringBefore(",").trim()
                    country = resolved.country ?: country
                    cityLat = resolved.latitude.toString()
                    cityLng = resolved.longitude.toString()
                },
                autocomplete = { q, s -> repo.autocompleteAddress(q, s) },
                resolvePlace = { p, s -> repo.getPlaceAddress(p, s) },
                label = "Город (пусто = авто)",
                placeholder = "Начните вводить город или адрес…",
            )
            if (cityLat != null && cityLng != null) {
                VSpace(6)
                Box(
                    Modifier.fillMaxWidth().sBorderRadius(8).sPadding(8, 12)
                        .styleModifier {
                            property("background", AppColors.Accent.SuccessTint.toRgbString())
                            property("border", "1px solid ${AppColors.Success.toRgbaString(0.22f)}")
                        }
                ) {
                    SpanText(
                        "📍 $city · $cityLat, $cityLng",
                        Modifier.bodySMedium().color(AppColors.Success),
                    )
                }
            }
            VSpace(10)
            TextArea(bio, { bio = it }, label = "Bio", placeholder = "авто", rows = 2)
            VSpace(12)
            ChipGroup(
                "Виды спорта",
                sports.map { it.name.lowercase() to it.name },
                selectedSports,
            ) { selectedSports = it }
            VSpace(10)
            ChipGroup("Языки", SEED_LANGUAGES.map { it to it }, languages) { languages = it }
            VSpace(10)
            ChipGroup("Активности", SEED_ACTIVITIES, activities) { activities = it }
            VSpace(10)
            ChipGroup("Интересы", SEED_LIFESTYLE, lifestyle) { lifestyle = it }

            error?.let {
                VSpace(10)
                StatusBanner(it, isError = true)
            }
            VSpace(16)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f))
                StudioActionButton("Отмена", enabled = !saving) { onClose() }
                Box(Modifier.width(10.px))
                StudioActionButton(
                    if (saving) "Сохраняем…" else if (existing == null) "Создать пользователя" else "Сохранить изменения",
                    enabled = !saving,
                    primary = true,
                ) {
                    saving = true
                    error = null
                    scope.launch {
                        try {
                            val request = SeedCustomUserRequest(
                                fullName = fullName.takeIf { it.isNotBlank() },
                                username = username.takeIf { it.isNotBlank() },
                                email = email.takeIf { it.isNotBlank() },
                                gender = gender.takeIf { it != RANDOM },
                                bio = bio.takeIf { it.isNotBlank() },
                                city = city.takeIf { it.isNotBlank() },
                                cityLat = cityLat,
                                cityLng = cityLng,
                                country = country.takeIf { it.isNotBlank() },
                                dateOfBirth = dateOfBirth.takeIf { it.isNotBlank() },
                                languages = languages.toList(),
                                interestSports = selectedSports.toList(),
                                interestActivities = activities.toList(),
                                interestLifestyle = lifestyle.toList(),
                            )
                            val user =
                                if (existing == null) {
                                    repo.seedCustomUser(request)
                                } else {
                                    repo.updateSeedUser(existing.id, request)
                                }
                            if (existing == null) onClose()
                            onSaved(user, existing == null)
                        } catch (e: Throwable) {
                            error = e.message ?: "Не удалось сохранить пользователя"
                        } finally {
                            saving = false
                        }
                    }
                }
            }

            if (existing != null) {
                VSpace(20)
                CardAvatarSection(repo, scope, existing, onGenerated = { onSaved(existing, false) })
            }
        }
    }
}

/**
 * Card gallery + AI avatar generation for one fake user, mirroring the app's
 * flow: pick a card template (visual grid), attach a source photo, optional
 * prompt, generate.
 */
@Composable
private fun CardAvatarSection(
    repo: AdminRepository,
    scope: CoroutineScope,
    user: SeedUserRow,
    onGenerated: () -> Unit,
) {
    var templates by remember { mutableStateOf<List<SeedCardTemplate>?>(null) }
    var templateId by remember { mutableStateOf<String?>(null) }
    var prompt by remember { mutableStateOf("") }
    var photo by remember { mutableStateOf<Triple<ByteArray, String, String>?>(null) }
    var running by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf<Pair<String, Boolean>?>(null) }
    var avatarUrl by remember { mutableStateOf(user.avatarUrl) }

    LaunchedEffect(Unit) {
        runCatching {
            val loaded = repo.getSeedCardTemplates()
            templates = loaded
            templateId = loaded.firstOrNull { it.isDefault }?.id ?: loaded.firstOrNull()?.id
        }.onFailure { templates = emptyList() }
    }

    Column(
        Modifier.fillMaxWidth()
            .sBorderRadius(12)
            .padding(16.px)
            .styleModifier {
                property("background", AppColors.Background.toRgbString())
                property("border", "1px solid ${AppColors.Border.toRgbString()}")
            },
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            SeedAvatarBubble(avatarUrl, user.fullName, size = 44)
            Box(Modifier.width(12.px))
            Column {
                SpanText("Карточка и AI аватар", Modifier.bodyMMedium().color(AppColors.Ink))
                Box(Modifier.height(2.px))
                SpanText(
                    "Выберите карточку, прикрепите исходное фото и запустите генерацию — тот же конвейер, что в приложении.",
                    Modifier.bodySMedium().color(AppColors.Muted),
                )
            }
        }
        VSpace(14)
        when {
            templates == null -> SpanText("Загрузка карточек…", Modifier.bodySMedium().color(AppColors.Muted))
            templates!!.isEmpty() -> SpanText("Шаблоны карточек не найдены", Modifier.bodySMedium().color(AppColors.Error))
            else -> Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap)) {
                templates!!.forEach { card ->
                    Box(Modifier.margin(right = 8.px, bottom = 8.px)) {
                        CardTile(card, selected = card.id == templateId) { templateId = card.id }
                    }
                }
            }
        }
        VSpace(12)
        TextInput(
            prompt,
            { prompt = it },
            label = "Промпт (необязательно)",
            placeholder = "напр.: cartoon style, confident smile",
        )
        VSpace(10)
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            StudioActionButton(photo?.second ?: "Выбрать исходное фото…", enabled = !running) {
                pickImageFile { bytes, name, type -> photo = Triple(bytes, name, type) }
            }
            Box(Modifier.weight(1f))
            StudioActionButton(
                if (running) "Генерируем… (30–120с)" else "Сгенерировать аватар",
                enabled = !running && photo != null && templateId != null,
                primary = true,
            ) {
                val (bytes, name, type) = photo ?: return@StudioActionButton
                val card = templateId ?: return@StudioActionButton
                running = true
                status = null
                scope.launch {
                    try {
                        val result = repo.generateSeedAvatar(
                            userId = user.id,
                            cardTemplateId = card,
                            bytes = bytes,
                            fileName = name,
                            contentType = type,
                            prompt = prompt.takeIf { it.isNotBlank() },
                        )
                        avatarUrl = result.avatarUrl
                        status = "Аватар добавлен в «${result.cardTemplateName}»" to false
                        onGenerated()
                    } catch (e: Throwable) {
                        status = (e.message ?: "Ошибка генерации") to true
                    } finally {
                        running = false
                    }
                }
            }
        }
        status?.let { (msg, isError) ->
            VSpace(8)
            StatusBanner(msg, isError)
        }
    }
}

@Composable
private fun CardTile(card: SeedCardTemplate, selected: Boolean, onClick: () -> Unit) {
    Column(
        Modifier
            .cursor(Cursor.Pointer)
            .sBorderRadius(10)
            .padding(6.px)
            .styleModifier {
                if (selected) {
                    property("background", AppColors.Surface.toRgbString())
                    property("border", "2px solid ${AppColors.Ink.toRgbString()}")
                    property("box-shadow", "0 2px 8px ${AppColors.Ink.toRgbaString(0.10f)}")
                } else {
                    property("border", "2px solid transparent")
                }
                property("transition", "all 140ms ease")
            }
            .onClick { onClick() },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier.width(64.px).height(88.px).sBorderRadius(8)
                .styleModifier {
                    property("background-image", "url('${card.imageUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("background-color", AppColors.SurfaceMuted.toRgbString())
                },
        )
        Box(Modifier.height(5.px))
        SpanText(
            card.name + if (card.isDefault) "" else " ★",
            Modifier.bodySMedium()
                .color(if (selected) AppColors.Ink else AppColors.Muted)
                .styleModifier {
                    property("max-width", "76px")
                    property("text-align", "center")
                    if (selected) property("font-weight", "600")
                },
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Posts tab — one post at a time, same form as the app's create-post popup
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SeedPostsTab(
    repo: AdminRepository,
    scope: CoroutineScope,
    reloadToken: Int,
) {
    var users by remember { mutableStateOf<List<SeedUserRow>>(emptyList()) }
    var authorId by remember { mutableStateOf<String?>(null) }
    var mode by remember { mutableStateOf("post") } // "post" | "story"
    var layout by remember { mutableStateOf(1) }
    var slotUrls by remember { mutableStateOf<List<String?>>(List(3) { null }) }
    var uploadingSlot by remember { mutableStateOf<Int?>(null) }
    var caption by remember { mutableStateOf("") }
    var storyUrl by remember { mutableStateOf<String?>(null) }
    var uploadingStory by remember { mutableStateOf(false) }
    var submitting by remember { mutableStateOf(false) }
    var banner by remember { mutableStateOf<Pair<String, Boolean>?>(null) }

    LaunchedEffect(reloadToken) {
        runCatching {
            users = repo.getSeedUsers(0, 200).items
            if (authorId == null) authorId = users.firstOrNull()?.id
        }
    }

    val slots = slotsFor(layout)
    val author = users.firstOrNull { it.id == authorId }

    Column(Modifier.fillMaxWidth()) {
        SectionCard(
            title = "Публикации и истории",
            subtitle = "Создайте публикацию или историю от имени тестового пользователя — те же поля, что в приложении.",
        ) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                ChoiceChip("Публикация", active = mode == "post") { mode = "post" }
                Box(Modifier.width(6.px))
                ChoiceChip("История", active = mode == "story") { mode = "story" }
            }
            VSpace(14)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f)) {
                    Dropdown(
                        options = users.map {
                            DropdownOption(it.id, "${it.fullName} (@${it.username})")
                        },
                        selectedValue = authorId,
                        onSelect = { authorId = it.value },
                        label = "Автор",
                        placeholder = if (users.isEmpty()) "Нет тестовых пользователей" else "Выберите автора",
                    )
                }
                author?.city?.let {
                    Box(Modifier.width(12.px))
                    SpanText("📍 $it", Modifier.bodySMedium().color(AppColors.Muted))
                }
            }
            VSpace(12)

            if (mode == "story") {
                SpanText("Изображение", Modifier.bodySMedium().color(AppColors.Muted))
                Box(Modifier.height(6.px))
                PhotoSlot(
                    url = storyUrl,
                    uploading = uploadingStory,
                    onPick = {
                        pickImageFile { bytes, name, type ->
                            uploadingStory = true
                            scope.launch {
                                try {
                                    storyUrl = repo.uploadStoryImage(bytes, name, type)
                                } catch (e: Throwable) {
                                    banner = (e.message ?: "Ошибка загрузки") to true
                                } finally {
                                    uploadingStory = false
                                }
                            }
                        }
                    },
                    onClear = { storyUrl = null },
                )
                VSpace(14)
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.weight(1f))
                    StudioActionButton(
                        if (submitting) "Публикуем…" else "Опубликовать историю",
                        enabled = !submitting && authorId != null && storyUrl != null,
                        primary = true,
                    ) {
                        submitting = true
                        banner = null
                        scope.launch {
                            try {
                                repo.seedCustomStory(
                                    SeedCustomStoryRequest(authorId = authorId!!, imageUrl = storyUrl!!),
                                )
                                banner = "История опубликована от имени ${author?.fullName ?: "пользователя"}" to false
                                storyUrl = null
                            } catch (e: Throwable) {
                                banner = (e.message ?: "Не удалось опубликовать") to true
                            } finally {
                                submitting = false
                            }
                        }
                    }
                }
            } else {
                SpanText("Компоновка", Modifier.bodySMedium().color(AppColors.Muted))
                Box(Modifier.height(6.px))
                Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap)) {
                    POST_LAYOUTS.forEach { (id, name) ->
                        Box(Modifier.margin(right = 8.px, bottom = 8.px)) {
                            LayoutTile(id, name, active = layout == id) { layout = id }
                        }
                    }
                }
                VSpace(12)
                SpanText("Фото ($slots)", Modifier.bodySMedium().color(AppColors.Muted))
                Box(Modifier.height(6.px))
                Row(Modifier.fillMaxWidth()) {
                    repeat(slots) { index ->
                        val url = slotUrls.getOrNull(index)
                        Box(Modifier.margin(right = 10.px)) {
                            PhotoSlot(
                                url = url,
                                uploading = uploadingSlot == index,
                                onPick = {
                                    pickImageFile { bytes, name, type ->
                                        uploadingSlot = index
                                        scope.launch {
                                            try {
                                                val uploaded = repo.uploadPostImage(bytes, name, type)
                                                slotUrls = slotUrls.toMutableList().also { it[index] = uploaded }
                                            } catch (e: Throwable) {
                                                banner = (e.message ?: "Ошибка загрузки") to true
                                            } finally {
                                                uploadingSlot = null
                                            }
                                        }
                                    }
                                },
                                onClear = {
                                    slotUrls = slotUrls.toMutableList().also { it[index] = null }
                                },
                            )
                        }
                    }
                }
                VSpace(12)
                TextArea(caption, { caption = it }, label = "Подпись", placeholder = "Что происходит?", rows = 3)
                VSpace(14)
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.weight(1f))
                    StudioActionButton(
                        if (submitting) "Публикуем…" else "Опубликовать",
                        enabled = !submitting && authorId != null,
                        primary = true,
                    ) {
                        val images = slotUrls.take(slots).filterNotNull()
                        if (images.isEmpty() && caption.isBlank()) {
                            banner = "Добавьте подпись или заполните слоты фото" to true
                            return@StudioActionButton
                        }
                        if (images.isNotEmpty() && images.size < slots) {
                            banner = "Заполните все $slots слота фото для этой компоновки (или очистите их)" to true
                            return@StudioActionButton
                        }
                        submitting = true
                        banner = null
                        scope.launch {
                            try {
                                repo.seedCustomPost(
                                    SeedCustomPostRequest(
                                        authorId = authorId!!,
                                        content = caption,
                                        imageUrls = images,
                                        layout = if (images.isEmpty()) 1 else layout,
                                    ),
                                )
                                banner = "Публикация создана от имени ${author?.fullName ?: "пользователя"}" to false
                                caption = ""
                                slotUrls = List(3) { null }
                            } catch (e: Throwable) {
                                banner = (e.message ?: "Не удалось опубликовать") to true
                            } finally {
                                submitting = false
                            }
                        }
                    }
                }
            }
            banner?.let { (msg, isError) ->
                VSpace(10)
                StatusBanner(msg, isError)
            }
        }
    }
}

/**
 * Visual layout selector tile — number on top, cell-preview below, matching
 * the app's six post layouts.
 */
@Composable
private fun LayoutTile(id: Int, name: String, active: Boolean, onClick: () -> Unit) {
    val cell = if (active) AppColors.Ink.toRgbString() else AppColors.Border.toRgbString()

    @Composable
    fun cellBox(w: Int, h: Int) {
        Box(
            Modifier.width(w.px).height(h.px).sBorderRadius(3)
                .styleModifier { property("background", cell) },
        )
    }

    Column(
        Modifier
            .cursor(Cursor.Pointer)
            .sBorderRadius(10)
            .padding(8.px)
            .styleModifier {
                if (active) {
                    property("background", AppColors.Surface.toRgbString())
                    property("border", "2px solid ${AppColors.Ink.toRgbString()}")
                } else {
                    property("background", AppColors.Background.toRgbString())
                    property("border", "2px solid transparent")
                }
                property("transition", "all 140ms ease")
            }
            .onClick { onClick() },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(Modifier.width(52.px).height(52.px), contentAlignment = Alignment.Center) {
            when (id) {
                1 -> cellBox(32, 48)
                2 -> cellBox(48, 32)
                3 -> Row { cellBox(23, 48); Box(Modifier.width(4.px)); cellBox(23, 48) }
                4 -> Column { cellBox(48, 23); Box(Modifier.height(4.px)); cellBox(48, 23) }
                5 -> Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    cellBox(48, 23)
                    Box(Modifier.height(4.px))
                    Row { cellBox(23, 23); Box(Modifier.width(4.px)); cellBox(23, 23) }
                }
                else -> Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Row { cellBox(23, 23); Box(Modifier.width(4.px)); cellBox(23, 23) }
                    Box(Modifier.height(4.px))
                    cellBox(48, 23)
                }
            }
        }
        Box(Modifier.height(4.px))
        SpanText(
            name,
            Modifier.bodySMedium()
                .color(if (active) AppColors.Ink else AppColors.Muted)
                .styleModifier {
                    property("max-width", "72px")
                    property("text-align", "center")
                    property("font-size", "11px")
                    if (active) property("font-weight", "600")
                },
        )
    }
}

@Composable
private fun PhotoSlot(
    url: String?,
    uploading: Boolean,
    onPick: () -> Unit,
    onClear: () -> Unit,
) {
    Box(
        Modifier.width(96.px).height(96.px).sBorderRadius(10)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (url != null) {
                    property("background-image", "url('$url')")
                    property("background-size", "cover")
                    property("background-position", "center")
                } else {
                    property("background", AppColors.Background.toRgbString())
                    property("border", "1.5px dashed ${AppColors.Ink.toRgbaString(0.18f)}")
                }
            }
            .onClick {
                it.stopPropagation()
                if (url == null && !uploading) onPick() else if (url != null) onClear()
            },
        contentAlignment = Alignment.Center,
    ) {
        when {
            uploading -> SpanText("…", Modifier.bodyMMedium().color(AppColors.Muted))
            url == null -> LucideIcon(name = "image-plus", size = 20, color = AppColors.Faint.toRgbString())
            else -> Box(
                Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "4px")
                        property("right", "4px")
                        property("background", AppColors.Ink.toRgbaString(0.55f))
                        property("border-radius", "999px")
                        property("padding", "3px")
                    },
            ) {
                LucideIcon(name = "x", size = 12, color = AppColors.Surface.toRgbString())
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Events tab — one event at a time, same form as the app's create-event popup
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SeedEventsTab(
    repo: AdminRepository,
    scope: CoroutineScope,
    reloadToken: Int,
    onMutated: () -> Unit,
) {
    var users by remember { mutableStateOf<List<SeedUserRow>>(emptyList()) }
    var sports by remember { mutableStateOf<List<SportOption>>(emptyList()) }
    var banner by remember { mutableStateOf<Pair<String, Boolean>?>(null) }

    var page by remember { mutableStateOf(0) }
    var data by remember { mutableStateOf<PageResult<EventRow>?>(null) }
    var localReload by remember { mutableStateOf(0) }
    val loadState = rememberLoadState()
    val rowBusy = remember { mutableStateMapOf<String, Boolean>() }
    var formOpen by remember { mutableStateOf(false) }
    var editingEvent by remember { mutableStateOf<EventRow?>(null) }

    LaunchedEffect(reloadToken) {
        runCatching { users = repo.getSeedUsers(0, 200).items }
        runCatching { sports = repo.getSports().filter { it.name != "Other" } }
    }

    LaunchedEffect(page, reloadToken, localReload, loadState.reloadToken) {
        data = null
        loadState.load {
            data = repo.getSeedEvents(page, 25)
        }
    }

    Column(Modifier.fillMaxWidth()) {
        if (loadState.error != null) {
            ErrorCard(loadState)
        } else {
            SectionCard(
                title = "Тестовые события",
                subtitle = "События создаются от имени тестового организатора — те же поля, что в приложении.",
                actions = {
                    StudioActionButton("+ Новое событие", primary = true) { formOpen = true }
                },
            ) {
                banner?.let { (msg, isError) ->
                    StatusBanner(msg, isError)
                    VSpace(12)
                }

                DataTable(
                    items = data?.items.orEmpty(),
                    loading = data == null,
                    columns = listOf(
                        TableColumn<EventRow>("СОБЫТИЕ", 2f) { row ->
                            Column {
                                SpanText(row.title, Modifier.bodyMMedium().color(AppColors.Ink))
                                Box(Modifier.height(3.px))
                                SpanText(
                                    listOfNotNull(row.sport, row.city).joinToString(" · "),
                                    Modifier.bodySMedium().color(AppColors.Muted),
                                )
                            }
                        },
                        TableColumn<EventRow>("ОРГАНИЗАТОР", 1.1f) {
                            SpanText(it.organizer, Modifier.bodyMRegular().color(AppColors.Muted))
                        },
                        TableColumn<EventRow>("УЧАСТНИКИ", 0.8f) {
                            SpanText(
                                "${it.participants}/${it.maxParticipants}",
                                Modifier.bodyMRegular().color(AppColors.Muted),
                            )
                        },
                        TableColumn<EventRow>("НАЧАЛО", 1f) {
                            SpanText(
                                formatTimestampShort(it.startAt),
                                Modifier.bodyMRegular().color(AppColors.Muted),
                            )
                        },
                        TableColumn<EventRow>("СТАТУС", 0.9f) {
                            Pill(it.status, eventStatusPillTone(it.status))
                        },
                        TableColumn<EventRow>("", 1.2f) { row ->
                            val busy = rowBusy[row.id] == true
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                StudioActionButton("Изменить", small = true, enabled = !busy) {
                                    editingEvent = row
                                }
                                Box(Modifier.width(8.px))
                                StudioActionButton("Удалить", small = true, danger = true, enabled = !busy) {
                                    rowBusy[row.id] = true
                                    scope.launch {
                                        runCatching { repo.deleteSeedEvent(row.id) }
                                        rowBusy[row.id] = false
                                        localReload++
                                        onMutated()
                                    }
                                }
                            }
                        },
                    ),
                    emptyText = "Тестовых событий нет — создайте первое",
                )
                VSpace(16)
                PaginationBar(
                    page = page,
                    pageSize = 25,
                    total = data?.total ?: 0,
                    onPrev = { if (page > 0) page-- },
                    onNext = { if ((page + 1) * 25 < (data?.total ?: 0)) page++ },
                )
            }
        }
    }

    if (formOpen) {
        CustomEventModal(
            repo = repo,
            scope = scope,
            users = users,
            sports = sports,
            onClose = { formOpen = false },
            onCreated = { message ->
                formOpen = false
                banner = message to false
                localReload++
                onMutated()
            },
        )
    }

    editingEvent?.let { event ->
        EventEditModal(
            repo = repo,
            scope = scope,
            event = event,
            users = users,
            onClose = {
                editingEvent = null
                localReload++
            },
        )
    }
}

/**
 * Event editing: participant management (list, add specific fake users, add
 * random, remove) plus status actions. Opened from the events table.
 */
@Composable
private fun EventEditModal(
    repo: AdminRepository,
    scope: CoroutineScope,
    event: EventRow,
    users: List<SeedUserRow>,
    onClose: () -> Unit,
) {
    var participants by remember { mutableStateOf<List<SeedParticipant>?>(null) }
    var addUserId by remember { mutableStateOf<String?>(null) }
    var randomCount by remember { mutableStateOf(3) }
    var busy by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf(event.status) }
    var error by remember { mutableStateOf<String?>(null) }
    var reload by remember { mutableStateOf(0) }

    LaunchedEffect(reload) {
        runCatching { participants = repo.getSeedEventParticipants(event.id) }
            .onFailure { participants = emptyList() }
    }

    val participantIds = participants.orEmpty().map { it.userId }.toSet()
    val addable = users.filter { it.id !in participantIds }

    Modal(
        isOpen = true,
        onClose = { if (!busy) onClose() },
        title = "Редактировать «${event.title}»",
        maxWidth = 620.px,
        closeOnOverlayClick = false,
        modifier = Modifier.styleModifier {
            property("max-height", "88vh")
            property("overflow-y", "auto")
        },
    ) {
        Column(Modifier.fillMaxWidth()) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    listOfNotNull(event.sport, event.city, formatTimestampShort(event.startAt)).joinToString(" · "),
                    Modifier.bodySMedium().color(AppColors.Muted),
                )
                Box(Modifier.weight(1f))
                Pill(status, eventStatusPillTone(status))
                if (status == "NEW") {
                    Box(Modifier.width(10.px))
                    StudioActionButton("Отменить событие", small = true, danger = true, enabled = !busy) {
                        busy = true
                        scope.launch {
                            runCatching { repo.updateSeedEventStatus(event.id, "CANCELLED") }
                                .onSuccess { status = "CANCELLED" }
                            busy = false
                        }
                    }
                }
            }
            VSpace(16)
            SpanText(
                "Участники (${participants?.size ?: "…"})",
                Modifier.bodyMMedium().color(AppColors.Ink),
            )
            VSpace(8)
            when {
                participants == null -> SpanText("Загрузка…", Modifier.bodySMedium().color(AppColors.Muted))
                participants!!.isEmpty() -> SpanText("Участников пока нет", Modifier.bodySMedium().color(AppColors.Muted))
                else -> Column(Modifier.fillMaxWidth()) {
                    participants!!.forEach { p ->
                        Row(
                            Modifier.fillMaxWidth().padding(topBottom = 5.px),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            SeedAvatarBubble(p.avatarUrl, p.name, size = 28)
                            Box(Modifier.width(10.px))
                            SpanText(p.name, Modifier.bodyMRegular().color(AppColors.Ink))
                            Box(Modifier.width(8.px))
                            SpanText("@${p.username}", Modifier.bodySMedium().color(AppColors.Faint))
                            if (p.isAdmin) {
                                Box(Modifier.width(8.px))
                                Pill("ORGANIZER", PillTone.Accent)
                            }
                            if (p.status != "CONFIRMED") {
                                Box(Modifier.width(8.px))
                                Pill(p.status, PillTone.Warning)
                            }
                            Box(Modifier.weight(1f))
                            if (!p.isAdmin) {
                                StudioActionButton("Удалить", small = true, danger = true, enabled = !busy) {
                                    busy = true
                                    scope.launch {
                                        runCatching { repo.removeSeedParticipant(event.id, p.userId) }
                                        busy = false
                                        reload++
                                    }
                                }
                            }
                        }
                    }
                }
            }
            VSpace(14)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f)) {
                    Dropdown(
                        options = addable.map { DropdownOption(it.id, "${it.fullName} (@${it.username})") },
                        selectedValue = addUserId,
                        onSelect = { addUserId = it.value },
                        placeholder = if (addable.isEmpty()) "Все тестовые пользователи уже в списке" else "Выберите тестового пользователя…",
                    )
                }
                Box(Modifier.width(10.px))
                StudioActionButton("Добавить", enabled = !busy && addUserId != null, primary = true) {
                    val uid = addUserId ?: return@StudioActionButton
                    busy = true
                    scope.launch {
                        runCatching {
                            repo.addSeedParticipants(event.id, SeedParticipantsRequest(userIds = listOf(uid)))
                        }.onFailure { error = it.message }
                        addUserId = null
                        busy = false
                        reload++
                    }
                }
            }
            VSpace(10)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText("Или добавить", Modifier.bodyMRegular().color(AppColors.Muted))
                Box(Modifier.width(8.px))
                listOf(3, 5, 10).forEach { n ->
                    ChoiceChip("$n", active = randomCount == n) { randomCount = n }
                    Box(Modifier.width(6.px))
                }
                SpanText("случайных игроков", Modifier.bodyMRegular().color(AppColors.Muted))
                Box(Modifier.width(10.px))
                StudioActionButton("Добавить случайных", enabled = !busy) {
                    busy = true
                    scope.launch {
                        runCatching {
                            repo.addSeedParticipants(event.id, SeedParticipantsRequest(randomCount = randomCount))
                        }.onFailure { error = it.message }
                        busy = false
                        reload++
                    }
                }
            }
            error?.let {
                VSpace(10)
                StatusBanner(it, isError = true)
            }
            VSpace(16)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f))
                StudioActionButton("Готово", primary = true, enabled = !busy) { onClose() }
            }
        }
    }
}

@Composable
private fun CustomEventModal(
    repo: AdminRepository,
    scope: CoroutineScope,
    users: List<SeedUserRow>,
    sports: List<SportOption>,
    onClose: () -> Unit,
    onCreated: (String) -> Unit,
) {
    var creatorId by remember { mutableStateOf(users.firstOrNull()?.id ?: RANDOM) }
    var sportId by remember { mutableStateOf(RANDOM) }
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var isPrivate by remember { mutableStateOf(false) }
    var isPro by remember { mutableStateOf(false) }
    var format by remember { mutableStateOf("") }
    var maxPlayers by remember { mutableStateOf("") }
    var cost by remember { mutableStateOf("") }
    var date by remember { mutableStateOf("") }
    var timeFrom by remember { mutableStateOf("") }
    var timeTo by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var latitude by remember { mutableStateOf<Double?>(null) }
    var longitude by remember { mutableStateOf<Double?>(null) }
    // Derived from the address pick — no separate field.
    var city by remember { mutableStateOf<String?>(null) }
    var cover by remember { mutableStateOf<Triple<ByteArray, String, String>?>(null) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    Modal(
        isOpen = true,
        onClose = { if (!saving) onClose() },
        title = "Новое событие",
        maxWidth = 680.px,
        closeOnOverlayClick = false,
        modifier = Modifier.styleModifier {
            property("max-height", "88vh")
            property("overflow-y", "auto")
        },
    ) {
        Column(Modifier.fillMaxWidth()) {
            SpanText(
                "Те же поля, что в форме создания события в приложении. Пустые поля генерируются автоматически.",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
            VSpace(14)
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    Dropdown(
                        options = listOf(DropdownOption(RANDOM, "Случайный организатор")) +
                            users.map { DropdownOption(it.id, "${it.fullName} (@${it.username})") },
                        selectedValue = creatorId,
                        onSelect = { creatorId = it.value },
                        label = "Организатор",
                    )
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    Dropdown(
                        options = listOf(DropdownOption(RANDOM, "Случайный вид спорта")) +
                            sports.map { DropdownOption(it.id, it.name) },
                        selectedValue = sportId,
                        onSelect = { sportId = it.value },
                        label = "Вид спорта",
                    )
                }
            }
            VSpace(10)
            TextInput(title, { title = it }, label = "Название", placeholder = "авто")
            VSpace(10)
            TextArea(description, { description = it }, label = "Описание", placeholder = "авто", rows = 2)
            VSpace(10)
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(format, { format = it }, label = "Формат (на команду)", placeholder = "напр. 5")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(maxPlayers, { maxPlayers = it }, label = "Макс. участников", placeholder = "авто")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(cost, { cost = it }, label = "Стоимость", placeholder = "0.00")
                }
            }
            VSpace(10)
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(date, { date = it }, label = "Дата", placeholder = "ГГГГ-ММ-ДД, авто")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(timeFrom, { timeFrom = it }, label = "Начало", placeholder = "ЧЧ:ММ")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(timeTo, { timeTo = it }, label = "Конец (необязательно)", placeholder = "ЧЧ:ММ")
                }
            }
            VSpace(10)
            AddressAutocompleteField(
                value = address,
                onValueChange = { address = it; latitude = null; longitude = null; city = null },
                onResolved = {
                    address = it.address
                    latitude = it.latitude
                    longitude = it.longitude
                    city = it.city
                },
                autocomplete = { q, s -> repo.autocompleteAddress(q, s) },
                resolvePlace = { p, s -> repo.getPlaceAddress(p, s) },
                label = "Адрес (пусто = случайная площадка)",
            )
            if (latitude != null) {
                VSpace(6)
                Box(
                    Modifier.fillMaxWidth().sBorderRadius(8).sPadding(8, 12)
                        .styleModifier {
                            property("background", AppColors.Accent.SuccessTint.toRgbString())
                            property("border", "1px solid ${AppColors.Success.toRgbaString(0.22f)}")
                        }
                ) {
                    SpanText(
                        "📍 ${city ?: address.substringBefore(",")} · $latitude, $longitude",
                        Modifier.bodySMedium().color(AppColors.Success),
                    )
                }
            }
            VSpace(10)
            Column(Modifier.fillMaxWidth()) {
                SpanText("Обложка (необязательно)", Modifier.bodySMedium().color(AppColors.Muted))
                Box(Modifier.height(6.px))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    StudioActionButton(cover?.second ?: "Выбрать обложку…") {
                        pickImageFile { bytes, name, type -> cover = Triple(bytes, name, type) }
                    }
                }
            }
            VSpace(12)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                ChoiceChip("Закрытое", active = isPrivate) { isPrivate = !isPrivate }
                Box(Modifier.width(8.px))
                ChoiceChip("PRO событие", active = isPro) { isPro = !isPro }
            }
            error?.let {
                VSpace(10)
                StatusBanner(it, isError = true)
            }
            VSpace(16)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f))
                StudioActionButton("Отмена", enabled = !saving) { onClose() }
                Box(Modifier.width(10.px))
                StudioActionButton(if (saving) "Создаём…" else "Создать событие", enabled = !saving, primary = true) {
                    if (date.isNotBlank() && timeFrom.isBlank()) {
                        error = "Укажите время начала (ЧЧ:ММ) для выбранной даты"
                        return@StudioActionButton
                    }
                    saving = true
                    error = null
                    scope.launch {
                        try {
                            val imageUrl = cover?.let { (bytes, name, type) ->
                                repo.uploadEventImage(bytes, name, type)
                            }
                            val result = repo.seedEvent(
                                SeedEventRequest(
                                    creatorId = creatorId.takeIf { it != RANDOM },
                                    sportId = sportId.takeIf { it != RANDOM },
                                    title = title.takeIf { it.isNotBlank() },
                                    description = description.takeIf { it.isNotBlank() },
                                    dateTime = if (date.isNotBlank()) localToUtcIso(date, timeFrom) else null,
                                    endDateTime = if (date.isNotBlank() && timeTo.isNotBlank()) {
                                        localToUtcIso(date, timeTo)
                                    } else {
                                        null
                                    },
                                    city = city?.takeIf { it.isNotBlank() },
                                    maxPlayers = maxPlayers.toIntOrNull(),
                                    isPro = isPro,
                                    privacy = if (isPrivate) "PRIVATE" else "PUBLIC",
                                    format = format.toIntOrNull(),
                                    cost = cost.takeIf { it.isNotBlank() },
                                    address = address.takeIf { it.isNotBlank() && latitude != null },
                                    latitude = latitude?.toString(),
                                    longitude = longitude?.toString(),
                                    imageUrl = imageUrl,
                                ),
                            )
                            onCreated(
                                "Создано «${result.title}» — добавьте участников через «Изменить»",
                            )
                        } catch (e: Throwable) {
                            error = e.message ?: "Не удалось создать событие"
                        } finally {
                            saving = false
                        }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared widgets
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SeedAvatarBubble(avatarUrl: String?, name: String, size: Int = 36) {
    Box(
        Modifier.width(size.px).height(size.px).borderRadius(50.percent)
            .styleModifier {
                if (avatarUrl != null) {
                    property("background-image", "url('$avatarUrl')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    // Transparent avatar PNGs need a backdrop to read as a face.
                    property("background-color", AppColors.SurfaceMuted.toRgbString())
                } else {
                    property("background", AppColors.Background.toRgbString())
                    property("border", "1px solid ${AppColors.Border.toRgbString()}")
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        if (avatarUrl == null) {
            SpanText(
                name.firstOrNull()?.uppercase() ?: "?",
                Modifier.bodySMedium().color(AppColors.Ink)
                    .styleModifier { property("font-weight", "700") },
            )
        }
    }
}

/** Multi-select chips; [options] are (stored value → display label) pairs. */
@Composable
private fun ChipGroup(
    label: String,
    options: List<Pair<String, String>>,
    selected: Set<String>,
    onChange: (Set<String>) -> Unit,
) {
    Column(Modifier.fillMaxWidth()) {
        SpanText("$label (пусто = авто)", Modifier.bodySMedium().color(AppColors.Muted))
        Box(Modifier.height(6.px))
        Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap)) {
            options.forEach { (value, chipTitle) ->
                Box(Modifier.margin(right = 6.px, bottom = 6.px)) {
                    ChoiceChip(chipTitle, active = value in selected) {
                        onChange(if (value in selected) selected - value else selected + value)
                    }
                }
            }
        }
    }
}

@Composable
private fun ChoiceChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 12.px, topBottom = 6.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (active) {
                    property("background", AppColors.Ink.toRgbString())
                } else {
                    property("background", AppColors.Background.toRgbString())
                    property("border", "1px solid ${AppColors.Ink.toRgbaString(0.06f)}")
                }
                property("transition", "all 140ms ease")
            }
            .onClick { onClick() },
    ) {
        SpanText(
            label,
            Modifier.bodySMedium()
                .color(if (active) AppColors.Surface else AppColors.Muted)
                .styleModifier { if (active) property("font-weight", "600") },
        )
    }
}

/**
 * Inline action button used throughout the Content Studio tab views.
 * Replaces the old `StudioButton` helper; renamed to avoid collision with any
 * future top-level `StudioButton` composable.
 */
@Composable
private fun StudioActionButton(
    label: String,
    enabled: Boolean = true,
    primary: Boolean = false,
    danger: Boolean = false,
    small: Boolean = false,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(
                leftRight = if (small) 12.px else 16.px,
                topBottom = if (small) 6.px else 9.px,
            )
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                when {
                    danger -> {
                        property("background", AppColors.ErrorTint.toRgbString())
                        property("border", "1px solid ${AppColors.Error.toRgbaString(0.18f)}")
                    }
                    primary -> property("background", AppColors.Ink.toRgbString())
                    else -> {
                        property("background", AppColors.Surface.toRgbString())
                        property("border", "1px solid ${AppColors.Ink.toRgbaString(0.10f)}")
                    }
                }
                if (!enabled) property("opacity", "0.5")
                property("transition", "background 140ms ease")
            }
            .onClick {
                it.stopPropagation()
                if (enabled) onClick()
            },
    ) {
        SpanText(
            label,
            Modifier.bodySMedium()
                .color(
                    when {
                        danger -> AppColors.Error
                        primary -> AppColors.Surface
                        else -> AppColors.Ink
                    },
                )
                .styleModifier { property("font-weight", "600") },
        )
    }
}
